import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader,
  IconButton,
  Button,
  Divider,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Menu,
  MenuItem as MenuListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  useTheme
} from '@mui/material';
import { 
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Dashboard as DashboardIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Timeline as LineChartIcon,
  TableChart as TableChartIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useAuth } from '../../context/AuthContext';

const Visualizations = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [visualizations, setVisualizations] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedVisualization, setSelectedVisualization] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [visualizationType, setVisualizationType] = useState('');
  const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');
  const [newDashboardDescription, setNewDashboardDescription] = useState('');
  
  // Fetch visualizations from API
  useEffect(() => {
    fetchVisualizations();
    fetchDashboards();
  }, []);

  const fetchVisualizations = async () => {
    try {
      setLoading(true);
      setError('');
      
      // TODO: Replace with actual API call when visualization API is implemented
      // const response = await api.get('/api/visualizations');
      // setVisualizations(response.data);
      
      // For now, show no data instead of mock data
      setVisualizations([]);
      
    } catch (err) {
      console.error('Error fetching visualizations:', err);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please login again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view visualizations.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError(err.response?.data?.message || 'Failed to load visualizations. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboards = async () => {
    try {
      setError('');
      
      // TODO: Replace with actual API call when dashboard API is implemented
      // const response = await api.get('/api/dashboards');
      // setDashboards(response.data);
      
      // For now, show no data instead of mock data
      setDashboards([]);
      
    } catch (err) {
      console.error('Error fetching dashboards:', err);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please login again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view dashboards.');
      } else {
        setError(err.response?.data?.message || 'Failed to load dashboards. Please try again.');
      }
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleMenuOpen = (event, visualization) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedVisualization(visualization);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleOpenDialog = (type) => {
    setVisualizationType(type);
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setVisualizationType('');
  };
  
  const handleCreateVisualization = async () => {
    try {
      setError('');
      
      // TODO: Implement actual visualization creation API call
      // const response = await api.post('/api/visualizations', visualizationData);
      // setVisualizations([...visualizations, response.data]);
      
      setError('Visualization creation is not yet implemented. This feature requires backend API integration.');
      
    } catch (err) {
      console.error('Error creating visualization:', err);
      setError(err.response?.data?.message || 'Failed to create visualization.');
    } finally {
      handleCloseDialog();
    }
  };
  
  const handleOpenDashboardDialog = () => {
    setDashboardDialogOpen(true);
  };
  
  const handleCloseDashboardDialog = () => {
    setDashboardDialogOpen(false);
    setNewDashboardName('');
    setNewDashboardDescription('');
  };
  
  const handleCreateDashboard = async () => {
    try {
      setError('');
      
      // TODO: Implement actual dashboard creation API call
      // const response = await api.post('/api/dashboards', dashboardData);
      // setDashboards([...dashboards, response.data]);
      
      setError('Dashboard creation is not yet implemented. This feature requires backend API integration.');
      
    } catch (err) {
      console.error('Error creating dashboard:', err);
      setError(err.response?.data?.message || 'Failed to create dashboard.');
    } finally {
      handleCloseDashboardDialog();
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Helper to get the icon for visualization type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'line':
        return <LineChartIcon />;
      case 'bar':
        return <BarChartIcon />;
      case 'pie':
        return <PieChartIcon />;
      case 'table':
        return <TableChartIcon />;
      default:
        return <BarChartIcon />;
    }
  };
  
  const renderVisualization = (visualization) => {
    if (!visualization || !visualization.data || visualization.data.length === 0) {
      return (
        <Box sx={{ 
          height: 300, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'grey.50',
          borderRadius: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Box>
      );
    }

    // TODO: Implement actual visualization rendering when real data is available
    return (
      <Box sx={{ 
        height: 300, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: 'grey.50',
        borderRadius: 2
      }}>
        <Typography variant="body2" color="text.secondary">
          Visualization rendering not yet implemented
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
                Visualizations
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Create custom charts and dashboards for your data
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenDashboardDialog}
                sx={{ borderRadius: 2 }}
              >
                Create Dashboard
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('chart')}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
                }}
              >
                Create Visualization
              </Button>
            </Box>
          </Box>
        </Grid>
        
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          </Grid>
        )}
        
        <Grid item xs={12}>
          <Card 
            className="card"
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 120,
                    px: 3,
                    py: 2
                  }
                }}
              >
                <Tab label="My Visualizations" />
                <Tab label="Create New" />
                <Tab label="Dashboards" />
              </Tabs>
            </Box>
            
            {/* My Visualizations Tab */}
            {activeTab === 0 && (
              <Box sx={{ p: 3 }}>
                {loading ? (
                  <div className="connector-grid">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="skeleton-card">
                        <div className="skeleton-circle" style={{ marginBottom: '16px' }}></div>
                        <div className="skeleton-line" style={{ width: '80%', marginBottom: '12px' }}></div>
                        <div className="skeleton-line" style={{ width: '60%', marginBottom: '12px' }}></div>
                        <div className="skeleton-line" style={{ width: '40%' }}></div>
                      </div>
                    ))}
                  </div>
                ) : visualizations.length === 0 ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 8,
                    px: 3,
                    background: 'linear-gradient(135deg, var(--color-primary-a05) 0%, var(--color-secondary-a05) 100%)',
                    borderRadius: 3,
                    border: '2px dashed var(--color-primary-a20)'
                  }}>
                    <PieChartIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.7 }} />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                      No visualizations yet
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: '500px', mx: 'auto' }}>
                      Transform your data into beautiful, interactive charts and dashboards. Create your first visualization to unlock powerful insights from your connected data sources.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<BarChartIcon />}
                        onClick={() => handleOpenDialog('bar')}
                        sx={{ 
                          borderRadius: 2,
                          background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
                          px: 3,
                          py: 1.5
                        }}
                      >
                        Create Chart
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<DashboardIcon />}
                        onClick={handleOpenDashboardDialog}
                        sx={{ borderRadius: 2, px: 3, py: 1.5 }}
                      >
                        Create Dashboard
                      </Button>
                    </Box>
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        icon={<LineChartIcon />} 
                        label="Line Charts" 
                        variant="outlined" 
                        size="small"
                        onClick={() => handleOpenDialog('line')}
                        sx={{ cursor: 'pointer' }}
                      />
                      <Chip 
                        icon={<PieChartIcon />} 
                        label="Pie Charts" 
                        variant="outlined" 
                        size="small"
                        onClick={() => handleOpenDialog('pie')}
                        sx={{ cursor: 'pointer' }}
                      />
                      <Chip 
                        icon={<TableChartIcon />} 
                        label="Data Tables" 
                        variant="outlined" 
                        size="small"
                        onClick={() => handleOpenDialog('table')}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <div className="connector-grid">
                    {visualizations.map((viz) => (
                      <Card key={viz.id} className="connector-card" sx={{ borderRadius: 3 }}>
                        <CardContent className="connector-card-content">
                          <div className="connector-card-header">
                            <div className="connector-card-icon" style={{ backgroundColor: 'var(--color-primary-a10)' }}>
                              <PieChartIcon sx={{ color: 'primary.main' }} />
                            </div>
                            <div className="connector-card-info">
                              <Typography variant="h6" className="connector-card-title" title={viz.name}>
                                {viz.name}
                              </Typography>
                              <Typography variant="body2" className="connector-card-subtitle" color="text.secondary">
                                {viz.type}
                              </Typography>
                            </div>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, viz)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </div>
                          <Typography variant="body2" className="connector-card-description" color="text.secondary">
                            {viz.description || 'Custom visualization'}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </Box>
            )}
            
            {/* Create New Tab */}
            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Choose a visualization type
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Select the type of visualization you want to create
                </Typography>
                
                <Grid container spacing={3}>
                  {/* Line Chart */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card 
                      onClick={() => handleOpenDialog('line')}
                      sx={{ 
                        borderRadius: 3,
                        p: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                          bgcolor: 'var(--color-primary-a05)'
                        }
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          width: 64, 
                          height: 64, 
                          mb: 2,
                          bgcolor: 'var(--color-primary-a10)', 
                          color: theme.palette.primary.main
                        }}
                      >
                        <LineChartIcon fontSize="large" />
                      </Avatar>
                      <Typography variant="h6" align="center" gutterBottom>
                        Line Chart
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center">
                        Good for showing trends over time, like sales or traffic
                      </Typography>
                    </Card>
                  </Grid>
                  
                  {/* Bar Chart */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card 
                      onClick={() => handleOpenDialog('bar')}
                      sx={{ 
                        borderRadius: 3,
                        p: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                          bgcolor: 'var(--color-primary-a05)'
                        }
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          width: 64, 
                          height: 64, 
                          mb: 2,
                          bgcolor: 'var(--color-secondary-a10)', 
                          color: theme.palette.secondary.main
                        }}
                      >
                        <BarChartIcon fontSize="large" />
                      </Avatar>
                      <Typography variant="h6" align="center" gutterBottom>
                        Bar Chart
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center">
                        Perfect for comparing values across categories
                      </Typography>
                    </Card>
                  </Grid>
                  
                  {/* Pie Chart */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card 
                      onClick={() => handleOpenDialog('pie')}
                      sx={{ 
                        borderRadius: 3,
                        p: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                          bgcolor: 'var(--color-primary-a05)'
                        }
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          width: 64, 
                          height: 64, 
                          mb: 2,
                          bgcolor: 'rgba(255, 180, 0, 0.1)', 
                          color: theme.palette.warning.main
                        }}
                      >
                        <PieChartIcon fontSize="large" />
                      </Avatar>
                      <Typography variant="h6" align="center" gutterBottom>
                        Pie Chart
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center">
                        Ideal for showing proportions or percentages of a whole
                      </Typography>
                    </Card>
                  </Grid>
                  
                  {/* Table */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card 
                      onClick={() => handleOpenDialog('table')}
                      sx={{ 
                        borderRadius: 3,
                        p: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                          bgcolor: 'var(--color-primary-a05)'
                        }
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          width: 64, 
                          height: 64, 
                          mb: 2,
                          bgcolor: 'rgba(255, 82, 82, 0.1)', 
                          color: theme.palette.error.main
                        }}
                      >
                        <TableChartIcon fontSize="large" />
                      </Avatar>
                      <Typography variant="h6" align="center" gutterBottom>
                        Table
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center">
                        Best for displaying detailed data with multiple columns
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {/* Dashboards Tab */}
            {activeTab === 2 && (
              <Box sx={{ p: 3 }}>
                {dashboards.length === 0 ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 8,
                    px: 3,
                    background: 'linear-gradient(135deg, var(--color-secondary-a05) 0%, var(--color-primary-a05) 100%)',
                    borderRadius: 3,
                    border: '2px dashed var(--color-secondary-a20)'
                  }}>
                    <DashboardIcon sx={{ fontSize: 64, color: 'secondary.main', mb: 2, opacity: 0.7 }} />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                      No dashboards yet
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: '500px', mx: 'auto' }}>
                      Organize your visualizations into comprehensive dashboards. Create interactive reports that tell the complete story of your data.
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<DashboardIcon />}
                      onClick={handleOpenDashboardDialog}
                      sx={{ 
                        borderRadius: 2,
                        background: 'linear-gradient(45deg, var(--color-secondary) 30%, var(--color-secondary-light) 90%)',
                        px: 3,
                        py: 1.5
                      }}
                    >
                      Create Dashboard
                    </Button>
                  </Box>
                ) : (
                  <div className="connector-grid">
                    {dashboards.map((dashboard) => (
                      <Card key={dashboard.id} className="connector-card" sx={{ borderRadius: 3 }}>
                        <CardContent className="connector-card-content">
                          <div className="connector-card-header">
                            <div className="connector-card-icon" style={{ backgroundColor: 'var(--color-secondary-a10)' }}>
                              <DashboardIcon sx={{ color: 'secondary.main' }} />
                            </div>
                            <div className="connector-card-info">
                              <Typography variant="h6" className="connector-card-title" title={dashboard.name}>
                                {dashboard.name}
                              </Typography>
                              <Typography variant="body2" className="connector-card-subtitle" color="text.secondary">
                                {dashboard.visualizations?.length || 0} visualizations
                              </Typography>
                            </div>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, dashboard)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </div>
                          <Typography variant="body2" className="connector-card-description" color="text.secondary">
                            {dashboard.description || 'Custom dashboard'}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
      
      {/* Visualization Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            minWidth: 180,
            mt: 1,
          },
        }}
      >
        <MenuListItem onClick={handleMenuClose}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuListItem>
        <MenuListItem onClick={handleMenuClose}>
          <ListItemIcon>
            <DuplicateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuListItem>
        <MenuListItem onClick={handleMenuClose}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuListItem>
        <MenuListItem onClick={handleMenuClose}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuListItem>
        <Divider />
        <MenuListItem onClick={handleMenuClose}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuListItem>
      </Menu>
      
      {/* Create Visualization Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle>
          Create {visualizationType.charAt(0).toUpperCase() + visualizationType.slice(1)} Chart
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Visualization creation requires backend API implementation and real data connections. This feature will be available once your data connectors are properly integrated.
            </Alert>
            
            <Typography variant="h6" gutterBottom>
              Chart Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Chart Name"
                  required
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateVisualization}
            startIcon={<SaveIcon />}
            disabled
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
            }}
          >
            Create Chart
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Create Dashboard Dialog */}
      <Dialog
        open={dashboardDialogOpen}
        onClose={handleCloseDashboardDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle>Create New Dashboard</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Dashboard creation requires backend API implementation. This feature will be available once the API is fully integrated.
            </Alert>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Dashboard Name"
                  value={newDashboardName}
                  onChange={(e) => setNewDashboardName(e.target.value)}
                  required
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={newDashboardDescription}
                  onChange={(e) => setNewDashboardDescription(e.target.value)}
                  multiline
                  rows={3}
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDashboardDialog} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateDashboard}
            disabled
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
            }}
          >
            Create Dashboard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Visualizations;
